import React, { useEffect, useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import Footer from 'src/components/Footer'
import NewHeader from 'src/New Codes/components/NewHeader'
import { node_url, url } from 'src/helpers'
import './PublicPages.css'

const PAGE_SIZE = 10

function excerptOf(post) {
    if (post?.meta_description) return post.meta_description
    const text = (post?.body_html || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
    return text.length > 160 ? text.slice(0, 160).trim() + '…' : text
}

function formatDate(value) {
    return new Date(value).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
}

const PostMeta = ({ post, showMore }) => (
    <div className="bl-meta">
        <span><i className="fa fa-calendar-o"></i>{formatDate(post?.created_at)}</span>
        <span><i className="fa fa-eye"></i>{(post?.views || 0).toLocaleString()}</span>
        {showMore && <span className="bl-readmore">Read more <i className="fa fa-arrow-right" style={{ marginLeft: 4 }}></i></span>}
    </div>
)

const Cover = ({ post }) =>
    post?.cover_image ? (
        <img src={`${node_url}${post.cover_image}`} alt={post.title} />
    ) : (
        <div className="bl-ph"><i className="fa fa-newspaper-o"></i></div>
    )

const BlogList = () => {
    const [posts, setPosts] = useState([])
    const [categories, setCategories] = useState([])
    const [page, setPage] = useState(1)
    const [hasMore, setHasMore] = useState(false)
    const [loading, setLoading] = useState(true)
    const [loadingMore, setLoadingMore] = useState(false)

    const { search } = useLocation()
    const query = useMemo(() => new URLSearchParams(search), [search])
    const tag = query.get('tag') || ''
    const category = query.get('category') || ''

    async function fetchPage(pageNumber) {
        const params = new URLSearchParams({ page: pageNumber })
        if (tag) params.set('tag', tag)
        if (category) params.set('category', category)
        const response = await fetch(url + 'blog?' + params.toString())
        const data = await response.json()
        if (data?.status !== 200) throw new Error(data?.message || 'Failed to load blog posts')
        return data?.data || []
    }

    useEffect(() => {
        let cancelled = false
        async function load() {
            setLoading(true)
            try {
                const rows = await fetchPage(1)
                if (cancelled) return
                setPosts(rows)
                setPage(1)
                setHasMore(rows.length === PAGE_SIZE)
            } catch (err) {
                if (!cancelled) toast.error(err.message || 'Failed to load blog posts')
            } finally {
                if (!cancelled) setLoading(false)
            }
        }
        load()
        window.scrollTo(0, 0)
        return () => { cancelled = true }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tag, category])

    useEffect(() => {
        async function loadCategories() {
            try {
                const response = await fetch(url + 'blog')
                const data = await response.json()
                const names = (data?.data || []).map((post) => post.category).filter(Boolean)
                setCategories([...new Set(names)])
            } catch (err) {
                setCategories([])
            }
        }
        loadCategories()
    }, [])

    async function loadMore() {
        setLoadingMore(true)
        try {
            const rows = await fetchPage(page + 1)
            setPosts((current) => [...current, ...rows])
            setPage(page + 1)
            setHasMore(rows.length === PAGE_SIZE)
        } catch (err) {
            toast.error(err.message || 'Failed to load more posts')
        } finally {
            setLoadingMore(false)
        }
    }

    return (
        <>
            <NewHeader />
            <div className="public-page">
                <header className="pp-hero">
                    <div className="pp-wrap">
                        <span className="pp-eyebrow">Our blog</span>
                        <h1>Beba Mzigo Blogs</h1>
                        <p>News, tips, and insights from the logistics industry.</p>
                    </div>
                </header>

                <div className="pp-wrap pp-wide pp-body-section">
                    {categories.length > 1 && (
                        <div className="pp-filters">
                            <Link to="/blog" className={`pp-chip ${!category ? 'on' : ''}`}>All</Link>
                            {categories.map((name) => (
                                <Link key={name} to={`/blog?category=${encodeURIComponent(name)}`} className={`pp-chip ${category === name ? 'on' : ''}`}>{name}</Link>
                            ))}
                        </div>
                    )}
                    {tag && (
                        <div className="pp-filters">
                            <span className="pp-chip on">#{tag}</span>
                            <Link to="/blog" className="pp-chip">Clear filter</Link>
                        </div>
                    )}

                    {loading ? (
                        <div className="pp-state">Loading...</div>
                    ) : posts.length > 0 ? (
                        <>
                            <div className="bl-grid">
                                {posts.map((post) => (
                                    <Link to={`/blog/${post?.slug}`} className="bl-card" key={post?._id}>
                                        <div className="bl-thumb"><Cover post={post} /></div>
                                        <div className="bl-content">
                                            {post?.category && <span className="bl-pill">{post.category}</span>}
                                            <h3>{post?.title}</h3>
                                            <p className="bl-excerpt">{excerptOf(post)}</p>
                                            <PostMeta post={post} showMore />
                                        </div>
                                    </Link>
                                ))}
                            </div>

                            {hasMore && (
                                <button type="button" className="pp-btn ghost pp-more" onClick={loadMore} disabled={loadingMore}>
                                    {loadingMore ? 'Loading...' : 'Load more'}
                                </button>
                            )}
                        </>
                    ) : (
                        <div className="pp-state">
                            <i className="fa fa-newspaper-o"></i>
                            <p>No blog posts found{category || tag ? ' for this filter' : ' yet'}.</p>
                            {(category || tag) && <Link to="/blog" className="pp-btn ghost">See all blogs</Link>}
                        </div>
                    )}
                </div>
            </div>
            <Footer />
        </>
    )
}

export default BlogList
